import type { AccountSession } from '../../Store/StudentSlices/accountSessionsSlice';

type AccountSectionProps = {
  data: {
    registeredEmail: string;
    sessions: AccountSession[];
    isSessionsLoading: boolean;
    deletingSessionIds: string[];
    isLogoutAllLoading: boolean;
  };
  actions: {
    onChangePassword: () => void;
    onLogoutSession: (sessionId: string) => void;
    onLogoutAll: () => void;
  };
};

const AccountSection = ({ data, actions }: AccountSectionProps) => {
  const {
    registeredEmail,
    sessions,
    isSessionsLoading,
    deletingSessionIds,
    isLogoutAllLoading,
  } = data;
  const { onChangePassword, onLogoutSession, onLogoutAll } = actions;
  const getSessionTimestamp = (session: AccountSession) => {
    if (session.last_active) {
      const parsed = Date.parse(session.last_active);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
    return session.lastAccess ?? 0;
  };
  const currentSessionId = sessions.find((session) => session.is_current)?.id;
  const mostRecentSessionId =
    currentSessionId ??
    sessions.slice().sort((a, b) => getSessionTimestamp(b) - getSessionTimestamp(a))[0]
      ?.id;
  const hasSessions = sessions.length > 0;
  const showViewAll = sessions.length > 2;
  const accountRows = [
    {
      label: 'Registered Email',
      value: registeredEmail,
    },
    {
      label: 'Account Password',
      value: '••••••••',
      actionLabel: 'Change Password',
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-6">
        {accountRows.map((row) => (
          <div
            key={row.label}
            className="flex flex-wrap items-center justify-between gap-4"
          >
            <div className="max-w-[680px]">
              <p className="text-sm font-semibold text-slate-900">{row.label}</p>
              <p className="mt-1 text-sm text-slate-500">{row.value}</p>
            </div>
            {row.actionLabel ? (
              <button
                type="button"
                className="rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800"
                onClick={() => {
                  if (row.actionLabel === 'Change Password') {
                    onChangePassword();
                  }
                }}
              >
                {row.actionLabel}
              </button>
            ) : null}
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-900">Device Sessions</p>
          <p className="mt-1 text-sm text-slate-500">
            Manage and log out of your active sessions on other devices.
          </p>
        </div>
        <div className="border-b border-slate-200" />
    
        {!isSessionsLoading && !hasSessions ? (
          <div className="rounded-[12px] border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
            No sessions found.
          </div>
        ) : null}
        {hasSessions
          ? sessions.map((session) => {
              const deviceLabel = session.device?.trim();
              const clientName = Object.values(session.clients ?? {})[0];
              const formattedClientName = clientName
                ? clientName
                    .replace(/-/g, ' ')
                    .replace(/\b\w/g, (char) => char.toUpperCase())
                : 'Session';
              const sessionLabel = deviceLabel || formattedClientName;
              const locationValue =
                session.location?.trim() || session.ipAddress?.trim() || '';
              const locationLabel = locationValue || 'Unknown device';
              const isActive = session.id === mostRecentSessionId;
              const isDeleting = deletingSessionIds.includes(session.id);
              return (
                <div
                  key={session.id}
                  className="flex flex-wrap items-center justify-between gap-4"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {sessionLabel}
                      {isActive ? (
                        <span className="ml-2 inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                      ) : null}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">{locationLabel}</p>
                  </div>
                  <button
                    type="button"
                    className="rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={isDeleting}
                    onClick={() => onLogoutSession(session.id)}
                  >
                    {isDeleting ? 'Logging out...' : 'Log Out'}
                  </button>
                </div>
              );
            })
          : null}
        {hasSessions ? (
          <div
            className={`flex flex-wrap items-center gap-4 text-sm text-slate-500 ${
              showViewAll ? 'justify-between' : 'justify-end'
            }`}
          >
            {showViewAll ? (
              <button
                type="button"
                className="text-slate-500 hover:text-slate-700"
              >
                View all sessions
              </button>
            ) : null}
            <button
              type="button"
              className="rounded-[12px] border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-400 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isLogoutAllLoading}
              onClick={onLogoutAll}
            >
              {isLogoutAllLoading ? 'Logging out...' : 'Log out from all devices'}
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-rose-600">Delete Account</p>
          <p className="mt-1 text-sm text-slate-500">
            Once you delete your account, there is no going back. Please be certain.
          </p>
        </div>
        <button
          type="button"
          className="rounded-[12px] border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-500 transition hover:border-rose-400 hover:text-rose-600"
        >
          Delete Account
        </button>
      </div>
    </div>
  );
};

export default AccountSection;
